import { bulkContainerCheck, isContainerRepeated } from '@uturn/api/v1';
import { logErrorToSentry } from '@uturn/portal/lib/log-error-to-sentry';
import type { FormValues } from '@uturn/portal/modules/shipments/schema';
import {
	addValidationIssue,
	type ValidationResults,
	validationSuccess,
} from '@uturn/portal/types/validation';
import { getApiErrorMessage } from '@uturn/portal/utils';
import { z } from '@uturn/portal/zod';
import { useTranslation } from 'react-i18next';
import {
	containerNumberNonSOWNSchema,
	containerNumberSchema,
} from '../schema/container';

export const bulkCheckContainerNumbersInUse = async (
	containerNumbers: string[],
): Promise<string[]> => {
	const uniqueContainerNumbers = [
		...new Set(
			containerNumbers
				.map((containerNumber) => containerNumber.trim())
				.filter((containerNumber) => containerNumber !== ''),
		),
	];

	if (uniqueContainerNumbers.length === 0) {
		return [];
	}

	try {
		const { data } = await bulkContainerCheck(uniqueContainerNumbers);

		return data
			.filter((check) => check.repeated === true)
			.map((check) => check.containerNumber)
			.filter(
				(containerNumber): containerNumber is string =>
					containerNumber !== undefined,
			);
	} catch (err) {
		logErrorToSentry({
			error: err,
			context: 'bulk-container-check',
			level: 'warning',
		});

		return [];
	}
};

export function useContainerNumberValidation() {
	const { t } = useTranslation();

	/**
	 * validateContainerNumber
	 *
	 * - Must be a string.
	 * - Must be 11 characters long.
	 * - First 3 characters should be letters.
	 * - Last 7 characters should be numbers.
	 * - 4th character should be U, J or Z (unless shippingLine.code is 'SOWN').
	 *
	 * @param containerNumber
	 * @param shippingLineCode
	 * @returns
	 */
	const validateContainerNumber = (
		containerNumber: string,
		shippingLineCode?: string,
		validationResults?: ValidationResults<FormValues>,
	): ValidationResults<FormValues> => {
		let results = validationResults ?? validationSuccess;

		if (containerNumber.trim() === '') {
			return results;
		}

		const schema =
			shippingLineCode && shippingLineCode === 'SOWN'
				? containerNumberSchema
				: containerNumberNonSOWNSchema;
		try {
			schema.parse(containerNumber);
		} catch (err) {
			if (err instanceof z.ZodError) {
				results = addValidationIssue<FormValues>(
					results,
					err.issues[0].message,
					'equipment.containerNumber',
				);
			}
		}

		return results;
	};

	/**
	 * Check for duplicate container number.
	 *
	 * If a duplicate container number is detected:
	 * - We show a non-blocking inline error message.
	 * - But still allow the form to be submitted.
	 *
	 * @param containerNumber
	 * @param shipmentNumber
	 * @returns
	 */
	const validateContainerNumberDuplicate = async (
		containerNumber: string,
		shipmentNumber: number,
		validationResults?: ValidationResults<FormValues>,
	): Promise<ValidationResults<FormValues>> => {
		let results = validationResults ?? validationSuccess;

		if (containerNumber.trim() === '') {
			return results;
		}

		try {
			const response = await isContainerRepeated(
				containerNumber,
				shipmentNumber,
			);

			if (response.data) {
				results = addValidationIssue<FormValues>(
					results,
					t(
						'pages.addshipment.manual.container_in_use',
						'This container is already in use.',
					).toString(),
					'equipment.containerNumber',
				);
			}
		} catch (err) {
			results = addValidationIssue<FormValues>(
				results,
				getApiErrorMessage(err) ?? 'Something went wrong!',
				'equipment.containerNumber',
			);
		}

		return results;
	};
	return { validateContainerNumber, validateContainerNumberDuplicate };
}
